// src/components/SignInModal.js
//
// Sign in (mission 007, decision 02; REACT_APP_EMAIL_SIGNIN=on): rendered by
// WalletContext wherever setShowWalletModal(true) opens the sign-in modal.
//
//   Email   email → "Send code" (Supabase Auth OTP) → 6-digit code → "Verify"
//           → email-session. A NEW email first sees the separate-accounts
//           disclosure and must choose "Create account" (Astra 02 F1); a
//           returning email goes straight in.
//   Wallet  the existing connectors, under "or connect a wallet"; a connect
//           marks the intent and keeps the modal open: while the wallet is
//           connected and unsigned the email form and the connectors give way
//           to "Confirm it's you" (SignInStep, mission 009), whose Sign in tap
//           requests the SIWE signature; the modal closes once the session
//           exists. "Use another wallet" disconnects and returns here.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Mail, X } from 'lucide-react';
import ModalShell from './ui/modal-shell';
import SignInStep, { useSignInStep } from './SignInStep';
import { authService } from '../services/AuthService';
import { normalizeEmail, requestEmailSession, sendEmailCode, verifyEmailCode } from '../lib/emailSession';

const RESEND_S = 60;
const PRIMARY = 'flex h-10 w-full items-center justify-center gap-2 rounded-[10px] bg-white px-4 text-[14px] font-semibold text-black hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-50';
const FIELD = 'h-10 w-full rounded-[10px] border border-white/[0.08] bg-[#181818] px-3 text-[15px] text-white outline-none placeholder:text-zinc-600 focus:border-white/20';
const TEXT_BUTTON = 'h-9 px-2 text-[13px] text-zinc-400 hover:text-white disabled:opacity-40';
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function SignInModal({ isOpen, onClose, connectors = [], onConnect, signIn, onUseAnother, beforeEmailSession, isLoading }) {
  const [step, setStep] = useState('email');     // email | code | confirm
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resendAt, setResendAt] = useState(0);
  const [, tick] = useState(0);
  const accessToken = useRef(null);
  const mounted = useRef(true);
  const [stepActive, endStep] = useSignInStep(!!isOpen, !!signIn?.needed);
  const walletStep = stepActive && !!signIn?.address;
  useEffect(() => () => { mounted.current = false; }, []);

  // a fresh modal every time it opens
  useEffect(() => {
    if (!isOpen) return;
    setStep('email'); setCode(''); setError(null); setBusy(false); accessToken.current = null;
  }, [isOpen]);
  // the resend countdown
  useEffect(() => {
    if (step !== 'code' || Date.now() >= resendAt) return undefined;
    const t = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [step, resendAt]);

  const addr = normalizeEmail(email);
  const run = async (fn) => {
    setError(null); setBusy(true);
    try { await fn(); } catch (e) { if (mounted.current) setError(e?.message || 'Something went wrong. Try again.'); }
    finally { if (mounted.current) setBusy(false); }
  };

  const sendCode = () => run(async () => {
    await sendEmailCode(addr);
    setStep('code'); setCode(''); setResendAt(Date.now() + RESEND_S * 1000);
  });
  const finish = async (r) => {
    await beforeEmailSession?.();
    authService.setEmailSession(r);
    onClose?.();
  };
  const verify = () => run(async () => {
    accessToken.current = await verifyEmailCode(addr, code.trim());
    const r = await requestEmailSession(accessToken.current);
    if (r?.needs_account) { setStep('confirm'); return; }
    await finish(r);
  });
  const create = () => run(async () => {
    const r = await requestEmailSession(accessToken.current, { createAccount: true });
    await finish(r);
  });

  const waitS = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
  const wallets = connectors.filter((c) => c && c.name);

  return (
    <ModalShell open={!!isOpen} onClose={onClose} labelledBy="sign-in-title" size="sm">
      <div className="flex items-center justify-between border-b border-white/[0.05] px-5 py-4">
        <h2 id="sign-in-title" className="text-[17px] font-semibold tracking-tight">Sign in</h2>
        <button type="button" onClick={onClose} aria-label="Close"
          className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-500 hover:bg-white/[0.05] hover:text-white">
          <X size={16} />
        </button>
      </div>

      <div className="flex flex-col gap-4 px-5 py-5">
        {walletStep && (
          <SignInStep address={signIn.address} onDone={onClose}
            onUseAnother={async () => { await onUseAnother?.(); endStep(); }} />
        )}

        {!walletStep && step === 'email' && (
          <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (!busy && EMAIL_RX.test(addr)) sendCode(); }}>
            <label htmlFor="sign-in-email" className="text-[13px] font-medium text-zinc-400">Email</label>
            <input id="sign-in-email" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)} className={FIELD} />
            <p className="text-[13px] text-zinc-500">We'll email you a 6-digit code.</p>
            <p className="text-[13px] text-zinc-500">Your account is tied to this email.</p>
            <button type="submit" disabled={busy || !EMAIL_RX.test(addr)} className={PRIMARY}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send code
            </button>
          </form>
        )}

        {!walletStep && step === 'code' && (
          <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (!busy && /^\d{6}$/.test(code.trim())) verify(); }}>
            <p className="text-[14px] text-zinc-300">We sent a code to <span className="font-medium text-white">{addr}</span>.</p>
            <label htmlFor="sign-in-code" className="text-[13px] font-medium text-zinc-400">6-digit code</label>
            <input id="sign-in-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={`${FIELD} font-mono tracking-[0.3em]`} />
            <button type="submit" disabled={busy || !/^\d{6}$/.test(code.trim())} className={PRIMARY}>
              {busy && <Loader2 size={14} className="animate-spin" />} Verify
            </button>
            <div className="flex items-center justify-between">
              <button type="button" className={TEXT_BUTTON} onClick={() => { setStep('email'); setError(null); }}>Use another email</button>
              <button type="button" className={TEXT_BUTTON} disabled={busy || waitS > 0} onClick={sendCode}>
                {waitS > 0 ? `Resend in ${waitS}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {!walletStep && step === 'confirm' && (
          <div className="flex flex-col gap-3">
            <p className="text-[15px] font-medium text-white">No Lunexx account uses {addr} yet. Create one?</p>
            <p className="text-[13px] leading-relaxed text-zinc-400">
              Your account will be tied to this email. If you already use Lunexx with a wallet, that account's balance, purchases, referrals and history stay on the wallet — the two are not linked.
            </p>
            <button type="button" disabled={busy} onClick={create} className={PRIMARY}>
              {busy && <Loader2 size={14} className="animate-spin" />} Create account
            </button>
            <button type="button" className={`${TEXT_BUTTON} self-start`} disabled={busy}
              onClick={() => { setStep('email'); setCode(''); setError(null); accessToken.current = null; }}>Back</button>
          </div>
        )}

        {!walletStep && error && <p className="text-[13px] text-red-300/80" role="alert">{error}</p>}

        {!walletStep && step === 'email' && wallets.length > 0 && (
          <>
            <div className="flex items-center gap-3 text-[12px] text-zinc-600">
              <span className="h-px flex-1 bg-white/[0.06]" /> or connect a wallet <span className="h-px flex-1 bg-white/[0.06]" />
            </div>
            <div className="flex flex-col gap-2">
              {wallets.map((c) => (
                <button key={c.uid ?? c.id} type="button" disabled={isLoading} onClick={() => onConnect?.(c)}
                  className="flex h-10 w-full items-center justify-center rounded-[10px] border border-white/[0.08] bg-[#181818] px-4 text-[14px] font-medium text-white hover:bg-[#1D1D1D] disabled:opacity-50">
                  {c.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}
